import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, Eye, EyeOff, Loader2, CheckCircle2, KeyRound, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "BiteGoFood By VS-Solutions";
    return () => { document.title = prev; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!pwd) { toast.error("Enter your admin password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/login", { password: pwd });
      if (data?.user?.role !== "admin") {
        toast.error("This account is not authorized for the admin portal");
        setBusy(false);
        return;
      }
      login(data.token, data.user);
      toast.success("Authentication successful");
      nav("/admin");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error("Too many login attempts. Please try again later.");
      else toast.error("Invalid admin credentials");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0b0d12] text-white relative overflow-hidden flex flex-col" data-testid="admin-login-page">
      {/* ambient gradient + grain */}
      <div className="pointer-events-none absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#FF6B35]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-48 -left-40 w-[560px] h-[560px] rounded-full bg-[#ff8c42]/10 blur-[130px]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      <button onClick={() => nav("/login")} className="absolute top-5 left-5 z-20 flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors" data-testid="admin-back-btn">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex-1 flex items-center justify-center px-5 py-14 relative z-10">
        <div className="w-full max-w-[420px]">
          {/* brand */}
          <div className="flex flex-col items-center text-center mb-7">
            <div className="relative">
              <img src="/bitego-icon.png" alt="BiteGoFood" className="w-16 h-16 rounded-2xl shadow-[0_8px_40px_rgba(255,107,53,0.35)] ring-1 ring-white/10" />
              <span className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#0b0d12] ring-1 ring-white/10 flex items-center justify-center">
                <ShieldCheck size={13} className="text-[#FF6B35]" />
              </span>
            </div>
            <h1 className="mt-4 font-display font-extrabold text-2xl tracking-tight">
              BiteGoFood <span className="text-[#FF6B35]">Admin Management</span>
            </h1>
            <p className="text-white/45 text-sm mt-1">Secure access to the BiteGoFood platform</p>
          </div>

          {/* glass card */}
          <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white/70 mb-5">
              <span className="h-px flex-1 bg-white/10" />
              <KeyRound size={14} className="text-[#FF6B35]" /> Administrator Sign In
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <label className="block text-xs font-semibold text-white/50 mb-1.5">Password</label>
            <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-white/10 focus-within:border-[#FF6B35]/70 focus-within:ring-2 ring-[#FF6B35]/20 px-3.5 transition-all">
              <Lock size={16} className="text-white/30" />
              <input
                type={show ? "text" : "password"} autoComplete="current-password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="flex-1 bg-transparent py-3 text-sm outline-none placeholder:text-white/30"
                placeholder="Enter your admin password" data-testid="admin-password"
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="text-white/40 hover:text-white transition-colors" data-testid="admin-password-toggle" aria-label={show ? "Hide password" : "Show password"}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <button
              type="submit" disabled={busy}
              className="mt-6 w-full rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] hover:from-[#ff7a48] hover:to-[#ff9a58] text-white font-bold py-3.5 flex items-center justify-center gap-2 shadow-[0_10px_30px_rgba(255,107,53,0.35)] transition-all active:scale-[0.99] disabled:opacity-70"
              data-testid="admin-login-btn"
            >
              {busy ? <><Loader2 size={17} className="animate-spin" /> Authenticating securely…</> : <><ShieldCheck size={17} /> Secure Admin Login</>}
            </button>

            <p className="text-[11px] text-white/35 text-center mt-4">Admin accounts are created and managed by Super Admin.</p>
          </form>

          {/* security indicators */}
          <div className="mt-5 grid grid-cols-3 gap-2">
            {[[Lock, "Secure Authentication"], [ShieldCheck, "Role-Based Access"], [CheckCircle2, "Protected Portal"]].map(([Icon, label]) => (
              <div key={label} className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-2 py-3 flex flex-col items-center text-center gap-1.5">
                <Icon size={15} className="text-[#FF6B35]" />
                <span className="text-[10px] leading-tight text-white/50 font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="relative z-10 text-center text-[11px] text-white/30 pb-6">A Product by Vijay Software Solutions Pvt Ltd</p>
    </div>
  );
}
